import { useContext, useEffect, useState } from 'react';
import { Context } from '@context/context';
import { IState } from '@context/stateModels';
import { StepperBarLabels } from '@context/labelContext';
import { macroStepList, StepToMacroStepMap, MacroStepKey, getMacroFillPercent } from '@utils/StepsMap';
import { useCommonLabels } from '@hooks/useCommonLabels';
import styles from './StepperSidebar.module.scss';

type MacroStepState = 'completed' | 'active' | 'pending';

const getMacroLabel = (key: MacroStepKey, field: 'Title' | 'Subtitle', labels?: StepperBarLabels) =>
  labels?.[`${key}${field}` as keyof StepperBarLabels];

const getMacroStepState = (order: number, currentOrder?: number): MacroStepState => {
  if (!currentOrder) {
    return 'pending';
  }
  if (order < currentOrder) {
    return 'completed';
  }
  return order === currentOrder ? 'active' : 'pending';
};

// Fill of the line below the bullet: full for completed macro-areas, empty for future ones,
// proportional to the page reached inside the active one (same rule as StepBar: both go
// through getMacroFillPercent).
const getLineFillPercent = (state: MacroStepState, macroKey: MacroStepKey, actualStep: string) => {
  if (state === 'completed') {
    return 100;
  }
  if (state === 'pending') {
    return 0;
  }
  return getMacroFillPercent(actualStep, macroKey);
};

export const getSidebarStates = (actualStep: string, labels?: StepperBarLabels) => {
  // The rail always follows the real actualStep, going back included (#26994 monotonic guard
  // removed on Sofia's request).
  const currentMacroKey = StepToMacroStepMap[actualStep];
  const currentOrder = macroStepList.find(macro => macro.key === currentMacroKey)?.order || undefined;

  return macroStepList.map(macro => {
    const state = getMacroStepState(macro.order, currentOrder);
    return {
      ...macro,
      title: getMacroLabel(macro.key, 'Title', labels),
      subtitle: getMacroLabel(macro.key, 'Subtitle', labels),
      state,
      lineFillPercent: getLineFillPercent(state, macro.key, actualStep)
    };
  });
};

const StepperSidebar = () => {
  const { state } = useContext<IState>(Context);
  const { stepperBar } = useCommonLabels();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) {
    // SSR always renders with the default actualStep (browser-only state like devUtils/session
    // isn't available server-side); painting real data only after mount avoids a hydration
    // mismatch that React 17 logs but never repaints on the initial hydrate pass.
    return null;
  }

  const items = getSidebarStates(state.stepManager?.actualStep, stepperBar);

  return (
    <div className={styles.slot}>
      {/*
        #26994 §6.2: il rail e' un indicatore di avanzamento in sola lettura, nessun elemento
        e' cliccabile. Percio' lista ordinata con aria-current="step" e non <nav>, che
        implicherebbe link di navigazione, e nessun <button>/<a>/aria-disabled: gli step non
        ancora raggiunti non sono controlli "disabilitati", semplicemente non sono raggiunti.
      */}
      <ol className={styles.sidebar}>
        {items.map((item, index) => (
          <li
            key={item.key}
            className={`${styles.item} ${styles[item.state] || ''}`}
            aria-current={item.state === 'active' ? 'step' : undefined}
          >
            <div className={styles.iconColumn}>
              <span className={styles.bullet} />
              {index < items.length - 1 && (
                <span className={styles.line}>
                  <span className={styles.lineFill} style={{ height: `${item.lineFillPercent}%` }} />
                </span>
              )}
            </div>
            <div className={styles.textColumn}>
              <span className={styles.title}>{item.title}</span>
              {item.state !== 'completed' && <span className={styles.subtitle}>{item.subtitle}</span>}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
};

export default StepperSidebar;
